// src/pages/Browse.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FiSearch, FiFilter, FiChevronDown, FiEye, FiThumbsUp } from 'react-icons/fi';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

function Browse() {
  const [questions, setQuestions] = useState([]);
  const [filteredQuestions, setFilteredQuestions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [stats, setStats] = useState(null);

  // --- Admin editing state ---
  const [adminPassword, setAdminPassword] = useState('');   // held in memory only, never stored
  const [adminUnlocked, setAdminUnlocked] = useState(false);
  const [showAdminPrompt, setShowAdminPrompt] = useState(false);
  const [adminError, setAdminError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editAnswer, setEditAnswer] = useState('');
  const [editQuestion, setEditQuestion] = useState('');
  const [editCategories, setEditCategories] = useState([]);
  const [savingEdit, setSavingEdit] = useState(false);

  // AI "suggest improvement" state
  const [questionSuggestion, setQuestionSuggestion] = useState(null);
  const [answerSuggestion, setAnswerSuggestion] = useState(null);
  const [suggestingField, setSuggestingField] = useState(null); // 'question' | 'answer' | null

  // Ask Claude to suggest a better version of the question or answer.
  const suggestImprovement = async (field) => {
    setSuggestingField(field);
    setAdminError(null);
    if (field === 'question') setQuestionSuggestion(null);
    if (field === 'answer') setAnswerSuggestion(null);
    try {
      const res = await axios.post(`${API_URL}/api/admin/suggest`, {
        password: adminPassword,
        type: field,
        text: field === 'question' ? editQuestion : editAnswer,
        question: editQuestion // context for answer improvement
      });
      if (field === 'question') setQuestionSuggestion(res.data.suggestion);
      if (field === 'answer') setAnswerSuggestion(res.data.suggestion);
    } catch (err) {
      setAdminError(err.response?.data?.error || 'Could not get a suggestion');
    } finally {
      setSuggestingField(null);
    }
  };

  // Clear suggestions when starting/cancelling an edit.
  const clearSuggestions = () => {
    setQuestionSuggestion(null);
    setAnswerSuggestion(null);
  };

  // --- Manual merge / un-merge ---
  const [showMerge, setShowMerge] = useState(false);
  const [mergeSelected, setMergeSelected] = useState([]); // ids checked
  const [mergeKeepId, setMergeKeepId] = useState(null);
  const [mergeReason, setMergeReason] = useState('');
  const [merging, setMerging] = useState(false);
  const [mergeMsg, setMergeMsg] = useState(null);
  const [recommending, setRecommending] = useState(false);
  const [mergeRecommendation, setMergeRecommendation] = useState(null); // {keepId, reasoning}

  const getMergeRecommendation = async () => {
    if (mergeSelected.length < 2) {
      setMergeMsg('Select at least two questions first.');
      return;
    }
    setRecommending(true);
    setMergeMsg(null);
    setMergeRecommendation(null);
    try {
      const res = await axios.post(`${API_URL}/api/admin/recommend-merge-keep`, {
        password: adminPassword,
        ids: mergeSelected,
        reason: mergeReason
      });
      setMergeRecommendation({ keepId: res.data.keepId, reasoning: res.data.reasoning });
      // Pre-select the AI's pick (admin can still override before confirming).
      if (res.data.keepId) setMergeKeepId(res.data.keepId);
    } catch (err) {
      setMergeMsg(err.response?.data?.error || 'Could not get a recommendation');
    } finally {
      setRecommending(false);
    }
  };

  const toggleMergeSelect = (id) => {
    setMergeRecommendation(null); // selection changed; prior recommendation is stale
    setMergeSelected(prev => {
      const next = prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id];
      if (mergeKeepId && !next.includes(mergeKeepId)) setMergeKeepId(null);
      if (!mergeKeepId && next.length > 0) setMergeKeepId(next[0]);
      return next;
    });
  };

  const runMerge = async () => {
    if (mergeSelected.length < 2 || !mergeKeepId) {
      setMergeMsg('Select at least two questions and choose which one to keep.');
      return;
    }
    setMerging(true);
    setMergeMsg(null);
    try {
      const res = await axios.post(`${API_URL}/api/admin/merge`, {
        password: adminPassword,
        keepId: mergeKeepId,
        mergeIds: mergeSelected.filter(id => id !== mergeKeepId),
        reason: mergeReason
      });
      setMergeMsg(res.data.message || 'Merged.');
      setMergeSelected([]);
      setMergeKeepId(null);
      setMergeReason('');
      setMergeRecommendation(null);
      await loadAdminData();
    } catch (err) {
      setMergeMsg(err.response?.data?.error || 'Merge failed');
    } finally {
      setMerging(false);
    }
  };

  const runUnmerge = async (id) => {
    if (!window.confirm('Restore this question as its own separate entry? It will be set back to "awaiting answer" so it can be answered correctly.')) return;
    try {
      await axios.post(`${API_URL}/api/admin/unmerge`, { password: adminPassword, id });
      await loadAdminData();
    } catch (err) {
      setAdminError(err.response?.data?.error || 'Could not restore');
    }
  };

  // --- AI auto-categorization ---
  const [autoCategorizing, setAutoCategorizing] = useState(false);
  const [autoCatMsg, setAutoCatMsg] = useState(null);
  const [suggestingCats, setSuggestingCats] = useState(false);

  const runAutoCategorize = async () => {
    if (!window.confirm('Let Claude reorganize and reassign categories across all FAQs? This applies automatically (you can adjust any FAQ afterward).')) {
      return;
    }
    setAutoCategorizing(true);
    setAutoCatMsg(null);
    try {
      const res = await axios.post(`${API_URL}/api/admin/auto-categorize`, { password: adminPassword });
      setAutoCatMsg(res.data.message || 'Done.');
      await loadAdminData();
      try {
        const catRes = await axios.get(`${API_URL}/api/questions/categories/list`);
        setCategories(catRes.data.categories || []);
      } catch { /* non-fatal */ }
    } catch (err) {
      setAutoCatMsg(err.response?.data?.error || 'Auto-categorize failed');
    } finally {
      setAutoCategorizing(false);
    }
  };

  // Per-FAQ: ask Claude to suggest categories and pre-select them in the editor.
  const suggestCategories = async () => {
    setSuggestingCats(true);
    setAdminError(null);
    try {
      const res = await axios.post(`${API_URL}/api/admin/suggest-categories`, {
        password: adminPassword,
        question: editQuestion,
        answer: editAnswer
      });
      const cats = res.data.categories || [];
      if (cats.length > 0) {
        setEditCategories(prev => [...new Set([...prev, ...cats])]);
      }
    } catch (err) {
      setAdminError(err.response?.data?.error || 'Could not suggest categories');
    } finally {
      setSuggestingCats(false);
    }
  };

  // --- Bulk import state ---
  const [showImport, setShowImport] = useState(false);
  const [importText, setImportText] = useState('');
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState(null);

  // A small CSV parser that correctly handles quoted fields containing commas,
  // quotes, and newlines (which the FAQ answers do).
  const parseCSV = (text) => {
    const rows = [];
    let field = '';
    let record = [];
    let inQuotes = false;
    for (let i = 0; i < text.length; i++) {
      const c = text[i];
      const next = text[i + 1];
      if (inQuotes) {
        if (c === '"' && next === '"') { field += '"'; i++; }
        else if (c === '"') { inQuotes = false; }
        else { field += c; }
      } else {
        if (c === '"') { inQuotes = true; }
        else if (c === ',') { record.push(field); field = ''; }
        else if (c === '\r') { /* ignore */ }
        else if (c === '\n') { record.push(field); rows.push(record); field = ''; record = []; }
        else { field += c; }
      }
    }
    // last field/record
    if (field.length > 0 || record.length > 0) { record.push(field); rows.push(record); }
    return rows.filter(r => r.some(cell => (cell || '').trim() !== ''));
  };

  // Turn parsed CSV rows into import objects using the header row to map columns.
  const buildImportRows = (csvRows) => {
    if (csvRows.length < 2) return [];
    const headers = csvRows[0].map(h => h.trim().toLowerCase());
    const idx = (names) => headers.findIndex(h => names.some(n => h.includes(n)));
    const qi = idx(['question']);
    const ai = idx(['full answer', 'answer']);
    const si = idx(['short', 'summary', 'takeaway']);
    const ci = idx(['category']);
    // Prefer a column literally named "full answer" for the answer if present.
    const fullIdx = headers.findIndex(h => h.includes('full answer'));
    const answerIdx = fullIdx !== -1 ? fullIdx : ai;
    // Short answer column (only if distinct from the full answer column)
    const summaryIdx = (si !== -1 && si !== answerIdx) ? si : -1;

    const out = [];
    for (let r = 1; r < csvRows.length; r++) {
      const row = csvRows[r];
      const question = qi !== -1 ? (row[qi] || '').trim() : '';
      const answer = answerIdx !== -1 ? (row[answerIdx] || '').trim() : '';
      const summary = summaryIdx !== -1 ? (row[summaryIdx] || '').trim() : '';
      const category = ci !== -1 ? (row[ci] || '').trim() : '';
      if (!question || !answer) continue;
      out.push({ question, answer, summary, category });
    }
    return out;
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setImportText(ev.target.result || '');
    reader.readAsText(file);
  };

  const runImport = async () => {
    setImportMsg(null);
    const csvRows = parseCSV(importText);
    const rows = buildImportRows(csvRows);
    if (rows.length === 0) {
      setImportMsg('No valid rows found. Make sure the first line is a header row with Question, Full Answer, etc.');
      return;
    }
    if (!window.confirm(`Import ${rows.length} FAQ entr${rows.length === 1 ? 'y' : 'ies'}? They will be stored exactly as written.`)) {
      return;
    }
    setImporting(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/bulk-import`, {
        password: adminPassword,
        rows
      });
      setImportMsg(res.data.message || 'Done.');
      setImportText('');
      await loadAdminData();
    } catch (err) {
      setImportMsg(err.response?.data?.error || 'Import failed');
    } finally {
      setImporting(false);
    }
  };
  const [deduping, setDeduping] = useState(false);
  const [dedupeMsg, setDedupeMsg] = useState(null);

  // Find and hide duplicate questions automatically.
  const runDedupe = async () => {
    if (!window.confirm('Scan for duplicate questions and hide the extras from Browse? Nothing is deleted — you can unhide any of them afterward.')) {
      return;
    }
    setDeduping(true);
    setDedupeMsg(null);
    try {
      const res = await axios.post(`${API_URL}/api/admin/dedupe`, { password: adminPassword });
      setDedupeMsg(res.data.message || 'Done.');
      await loadAdminData();
    } catch (err) {
      setDedupeMsg(err.response?.data?.error || 'Dedupe failed');
    } finally {
      setDeduping(false);
    }
  };

  // Unlock admin mode by verifying the password with the backend.
  const handleAdminUnlock = async () => {
    setAdminError(null);
    try {
      await axios.post(`${API_URL}/api/admin/verify`, { password: adminPassword });
      setAdminUnlocked(true);
      setShowAdminPrompt(false);
      await loadAdminData(); // load everything, including hidden entries
    } catch (err) {
      setAdminError('Incorrect password');
    }
  };

  // Load ALL questions (including hidden) for admin management.
  const loadAdminData = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/admin/questions/all`, { password: adminPassword });
      setQuestions(res.data.questions || []);
    } catch (err) {
      // If this fails, fall back to the normal public list.
      console.error('Could not load admin question list:', err);
    }
  };

  // Hide or show a question in Browse.
  const toggleVisibility = async (questionId, currentlyVisible) => {
    setAdminError(null);
    try {
      await axios.post(`${API_URL}/api/admin/questions/${questionId}/visibility`, {
        password: adminPassword,
        hidden: currentlyVisible // if it's currently visible, we're hiding it
      });
      await loadAdminData();
    } catch (err) {
      setAdminError(err.response?.data?.error || 'Could not change visibility');
    }
  };

  // PERMANENTLY delete a question (with confirmation).
  const deleteQuestion = async (questionId) => {
    if (!window.confirm('Permanently delete this question and its answer? This CANNOT be undone. (To remove it but keep it recoverable, use "Hide from Browse" instead.)')) {
      return;
    }
    setAdminError(null);
    try {
      await axios.post(`${API_URL}/api/admin/questions/${questionId}/delete`, {
        password: adminPassword
      });
      await loadAdminData();
    } catch (err) {
      setAdminError(err.response?.data?.error || 'Could not delete the question');
    }
  };

  // Answer a pending question directly from the portal.
  const [answeringId, setAnsweringId] = useState(null);
  const [pendingAnswer, setPendingAnswer] = useState('');
  const [savingAnswer, setSavingAnswer] = useState(false);

  const startAnswering = (q) => {
    setAnsweringId(q.id);
    setPendingAnswer('');
    setAdminError(null);
  };

  const cancelAnswering = () => {
    setAnsweringId(null);
    setPendingAnswer('');
  };

  const submitAnswer = async (questionId) => {
    if (!pendingAnswer.trim()) return;
    setSavingAnswer(true);
    setAdminError(null);
    try {
      await axios.post(`${API_URL}/api/admin/questions/${questionId}/answer`, {
        password: adminPassword,
        answer: pendingAnswer
      });
      setAnsweringId(null);
      setPendingAnswer('');
      await loadAdminData();
    } catch (err) {
      setAdminError(err.response?.data?.error || 'Could not save the answer');
    } finally {
      setSavingAnswer(false);
    }
  };

  const handleAdminLock = () => {
    setAdminUnlocked(false);
    setAdminPassword('');
    setEditingId(null);
    loadData(); // back to the normal public list
  };

  const startEditing = (q) => {
    setEditingId(q.id);
    setEditAnswer(q.answer || '');
    setEditQuestion(q.normalized_question || q.original_question || '');
    setEditCategories(getQuestionCategories(q));
    clearSuggestions();
    setAdminError(null);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditAnswer('');
    setEditQuestion('');
    clearSuggestions();
  };

  const saveEdit = async (questionId) => {
    setSavingEdit(true);
    setAdminError(null);
    try {
      await axios.post(`${API_URL}/api/admin/questions/${questionId}/edit`, {
        password: adminPassword,
        answer: editAnswer,
        normalized_question: editQuestion,
        categories: editCategories
      });
      setEditingId(null);
      await loadAdminData(); // refresh to show the saved edit (admin view)
    } catch (err) {
      setAdminError(err.response?.data?.error || 'Could not save the edit');
    } finally {
      setSavingEdit(false);
    }
  };

  // Load data on mount
  useEffect(() => {
    loadData();
  }, []);

  // Filter whenever search or category changes
  useEffect(() => {
    filterQuestions();
  }, [searchTerm, selectedCategory, selectedStatus, questions]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Fetch all canonical questions (answered AND pending). Pending ones are
      // shown marked as "awaiting answer".
      const questionsRes = await axios.get(`${API_URL}/api/questions`);
      setQuestions(questionsRes.data.questions || []);

      // Fetch categories
      const categoriesRes = await axios.get(`${API_URL}/api/questions/categories/list`);
      setCategories(categoriesRes.data.categories || []);

      // Fetch stats
      const statsRes = await axios.get(`${API_URL}/api/questions/stats/overview`);
      setStats(statsRes.data.stats);

      // Fetch resources for cross-surfacing (related resources per FAQ).
      try {
        const resourcesRes = await axios.get(`${API_URL}/api/resources`);
        setResources(resourcesRes.data.resources || []);
      } catch { /* non-fatal: resources are optional */ }

    } catch (err) {
      setError(err.message);
      console.error('Error loading FAQ data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Return a question's categories as an array. Uses the new multi-category
  // `categories` field, falling back to the legacy single `category`.
  const getQuestionCategories = (q) => {
    const raw = (q.categories && q.categories.trim()) ? q.categories : (q.category || '');
    return raw.split(',').map(c => c.trim()).filter(Boolean);
  };

  // Cross-surfacing: resources that share at least one category with this FAQ.
  const relatedResources = (q) => {
    const qc = new Set(getQuestionCategories(q).map(c => c.toLowerCase()));
    if (qc.size === 0) return [];
    return resources.filter(r => {
      const rc = (r.categories || '').split(',').map(c => c.trim().toLowerCase()).filter(Boolean);
      return rc.some(c => qc.has(c));
    }).slice(0, 4);
  };

  const filterQuestions = () => {
    let filtered = questions;

    // Filter by search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(q =>
        (q.normalized_question && q.normalized_question.toLowerCase().includes(term)) ||
        (q.original_question && q.original_question.toLowerCase().includes(term)) ||
        (q.answer && q.answer.toLowerCase().includes(term))
      );
    }

    // Filter by category — match against the question's full category list
    // (a FAQ can belong to multiple categories).
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(q => getQuestionCategories(q).includes(selectedCategory));
    }

    // Filter by status (answered / pending / all)
    if (selectedStatus !== 'all') {
      filtered = filtered.filter(q => q.status === selectedStatus);
    }

    // Sort by view count (most popular first)
    filtered.sort((a, b) => (b.view_count || 0) - (a.view_count || 0));

    setFilteredQuestions(filtered);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin text-indigo-600 mb-4" style={{ fontSize: '2rem' }}>⏳</div>
          <p className="text-gray-600">Loading FAQs...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <p className="text-red-700">Error loading FAQs: {error}</p>
        <button
          onClick={loadData}
          className="mt-4 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">FAQ Database</h2>
          <p className="text-gray-600">
            Browse answers to commonly asked questions about garage doors and our services.
          </p>
        </div>
        {/* Admin unlock control */}
        <div className="ml-4 shrink-0">
          {!adminUnlocked ? (
            <button
              onClick={() => { setShowAdminPrompt(!showAdminPrompt); setAdminError(null); }}
              className="text-sm text-gray-500 hover:text-gray-800 underline"
            >
              Admin
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-green-700 bg-green-50 px-2 py-1 rounded">Admin mode</span>
              <button
                onClick={runDedupe}
                disabled={deduping}
                className="text-sm text-indigo-600 hover:text-indigo-800 underline disabled:opacity-50"
              >
                {deduping ? 'Scanning…' : 'Find & hide duplicates'}
              </button>
              <button
                onClick={() => { setShowMerge(!showMerge); setMergeMsg(null); }}
                className="text-sm text-indigo-600 hover:text-indigo-800 underline"
              >
                Merge duplicates
              </button>
              <button
                onClick={runAutoCategorize}
                disabled={autoCategorizing}
                className="text-sm text-indigo-600 hover:text-indigo-800 underline disabled:opacity-50"
              >
                {autoCategorizing ? 'Categorizing…' : 'Auto-categorize'}
              </button>
              <button
                onClick={() => { setShowImport(!showImport); setImportMsg(null); }}
                className="text-sm text-indigo-600 hover:text-indigo-800 underline"
              >
                Bulk import
              </button>
              <button onClick={handleAdminLock} className="text-sm text-gray-500 hover:text-gray-800 underline">
                Lock
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Dedupe result message */}
      {dedupeMsg && adminUnlocked && (
        <div className="mb-6 bg-indigo-50 border border-indigo-200 rounded-lg p-3 max-w-xl text-sm text-indigo-800">
          {dedupeMsg}
        </div>
      )}

      {/* Auto-categorize result message */}
      {autoCatMsg && adminUnlocked && (
        <div className="mb-6 bg-indigo-50 border border-indigo-200 rounded-lg p-3 max-w-xl text-sm text-indigo-800">
          {autoCatMsg}
        </div>
      )}

      {/* Manual merge panel */}
      {showMerge && adminUnlocked && (
        <div className="mb-6 bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="font-medium text-gray-900 mb-1">Merge duplicate questions manually</h3>
          <p className="text-sm text-gray-600 mb-3">
            Check the questions that are really the same, choose which one to <span className="font-medium">keep</span>, and add a note on why. The others get hidden and point to the one you keep.
          </p>
          <div className="max-h-72 overflow-y-auto border border-gray-100 rounded mb-3">
            {questions.filter(q => q.is_canonical !== false).map(q => (
              <label key={q.id} className="flex items-start gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-50">
                <input
                  type="checkbox"
                  checked={mergeSelected.includes(q.id)}
                  onChange={() => toggleMergeSelect(q.id)}
                  className="mt-1"
                />
                <span className="flex-1 text-sm text-gray-800">
                  {q.normalized_question || q.original_question}
                  {mergeSelected.includes(q.id) && (
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); setMergeKeepId(q.id); }}
                      className={`ml-2 text-xs px-2 py-0.5 rounded-full border ${
                        mergeKeepId === q.id
                          ? 'bg-green-600 text-white border-green-600'
                          : 'bg-white text-gray-600 border-gray-300 hover:border-green-500'
                      }`}
                    >
                      {mergeKeepId === q.id ? '✓ Keep this' : 'Keep this'}
                    </button>
                  )}
                  {mergeRecommendation && mergeRecommendation.keepId === q.id && (
                    <span className="ml-2 text-xs text-indigo-600 font-medium">✨ AI pick</span>
                  )}
                </span>
              </label>
            ))}
          </div>
          <textarea
            value={mergeReason}
            onChange={(e) => setMergeReason(e.target.value)}
            rows={2}
            placeholder="Why are these the same question? (e.g. both ask about install duration, just worded differently). The AI uses this when picking the best version to keep."
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm mb-3"
          />

          {/* AI recommendation */}
          {mergeRecommendation && (
            <div className="mb-3 bg-indigo-50 border border-indigo-200 rounded p-3">
              <p className="text-sm text-indigo-800">
                <span className="font-medium">✨ AI recommends keeping:</span>{' '}
                {(() => {
                  const q = questions.find(x => x.id === mergeRecommendation.keepId);
                  return q ? (q.normalized_question || q.original_question) : '(selected)';
                })()}
              </p>
              {mergeRecommendation.reasoning && (
                <p className="text-xs text-indigo-700 mt-1">{mergeRecommendation.reasoning}</p>
              )}
              <p className="text-xs text-gray-600 mt-1">You can override by clicking “Keep this” on a different question above. The real answer is always preserved.</p>
            </div>
          )}

          {mergeMsg && <p className="text-sm text-indigo-700 mb-3">{mergeMsg}</p>}
          <div className="flex gap-2 items-center flex-wrap">
            {!mergeRecommendation ? (
              <button
                onClick={getMergeRecommendation}
                disabled={recommending || mergeSelected.length < 2}
                className="bg-indigo-600 text-white text-sm px-4 py-2 rounded hover:bg-indigo-700 disabled:opacity-50"
              >
                {recommending ? 'Evaluating…' : '✨ Recommend best version'}
              </button>
            ) : (
              <button
                onClick={runMerge}
                disabled={merging || mergeSelected.length < 2 || !mergeKeepId}
                className="bg-green-600 text-white text-sm px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50"
              >
                {merging ? 'Merging…' : `Confirm & merge ${mergeSelected.length} (keep selected)`}
              </button>
            )}
            <button
              onClick={() => { setShowMerge(false); setMergeSelected([]); setMergeKeepId(null); setMergeReason(''); setMergeMsg(null); setMergeRecommendation(null); }}
              className="text-sm text-gray-600 px-4 py-2 rounded border border-gray-300 hover:bg-gray-100"
            >
              Close
            </button>
            {mergeSelected.length >= 2 && (
              <span className="text-xs text-gray-500">{mergeSelected.length} selected</span>
            )}
          </div>
        </div>
      )}

      {/* Bulk import panel */}
      {showImport && adminUnlocked && (
        <div className="mb-6 bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="font-medium text-gray-900 mb-1">Bulk import FAQs</h3>
          <p className="text-sm text-gray-600 mb-3">
            Upload or paste CSV. The first row must be a header row. Recognized columns:
            <span className="font-medium"> Question, Full Answer (or Answer), Short Answer (summary), Category</span>.
            Entries are stored exactly as written.
          </p>
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={handleFileUpload}
            className="block text-sm mb-3"
          />
          <textarea
            value={importText}
            onChange={(e) => setImportText(e.target.value)}
            rows={6}
            placeholder="…or paste CSV here (including the header row)"
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono mb-3"
          />
          {importMsg && <p className="text-sm text-indigo-700 mb-3">{importMsg}</p>}
          <div className="flex gap-2">
            <button
              onClick={runImport}
              disabled={importing || !importText.trim()}
              className="bg-indigo-600 text-white text-sm px-4 py-2 rounded hover:bg-indigo-700 disabled:opacity-50"
            >
              {importing ? 'Importing…' : 'Import'}
            </button>
            <button
              onClick={() => { setShowImport(false); setImportText(''); setImportMsg(null); }}
              className="text-sm text-gray-600 px-4 py-2 rounded border border-gray-300 hover:bg-gray-100"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Admin password prompt */}
      {showAdminPrompt && !adminUnlocked && (
        <div className="mb-6 bg-white border border-gray-200 rounded-lg p-4 max-w-sm">
          <label className="block text-sm font-medium text-gray-700 mb-1">Admin password</label>
          <input
            type="password"
            value={adminPassword}
            onChange={(e) => setAdminPassword(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleAdminUnlock(); }}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            placeholder="Enter password to edit"
          />
          {adminError && <p className="text-red-600 text-sm mt-2">{adminError}</p>}
          <button
            onClick={handleAdminUnlock}
            className="mt-3 bg-indigo-600 text-white text-sm px-4 py-2 rounded hover:bg-indigo-700"
          >
            Unlock editing
          </button>
        </div>
      )}

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-gray-600 text-sm font-medium">Total Questions</p>
            <p className="text-2xl font-bold text-indigo-600">{stats.total_questions || 0}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-gray-600 text-sm font-medium">Answered</p>
            <p className="text-2xl font-bold text-green-600">{stats.answered_count || 0}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-gray-600 text-sm font-medium">Pending</p>
            <p className="text-2xl font-bold text-yellow-600">{stats.pending_count || 0}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-gray-600 text-sm font-medium">Total Views</p>
            <p className="text-2xl font-bold text-blue-600">{stats.total_views || 0}</p>
          </div>
        </div>
      )}

      {/* Search and Filter */}
      <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Search */}
          <div className="relative">
            <FiSearch className="absolute left-3 top-3 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search questions and answers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>

          {/* Category Filter */}
          <div className="relative">
            <FiFilter className="absolute left-3 top-3 text-gray-400" size={20} />
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent appearance-none"
            >
              <option value="all">All Categories</option>
              {categories.map(cat => (
                <option key={cat.id || cat.name} value={cat.name}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>

          {/* Status Filter */}
          <div className="relative">
            <FiFilter className="absolute left-3 top-3 text-gray-400" size={20} />
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent appearance-none"
            >
              <option value="all">All Statuses</option>
              <option value="answered">Answered</option>
              <option value="pending">Pending (awaiting answer)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="mb-8">
        <p className="text-gray-600 mb-4">
          Found <strong>{filteredQuestions.length}</strong> {filteredQuestions.length === 1 ? 'result' : 'results'}
        </p>

        {filteredQuestions.length === 0 ? (
          <div className="bg-gray-50 rounded-lg p-12 text-center">
            <p className="text-gray-600 mb-4">No questions found matching your search.</p>
            <p className="text-gray-500 text-sm">Try different keywords or browse all questions.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredQuestions.map(question => (
              <div key={question.id} className="bg-white rounded-lg shadow hover:shadow-md transition">
                {/* Question Header */}
                <button
                  onClick={() => setExpandedId(expandedId === question.id ? null : question.id)}
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition"
                >
                  <div className="flex-1 text-left">
                    <div className="flex items-start gap-3">
                      <FiChevronDown
                        size={20}
                        className={`text-indigo-600 flex-shrink-0 mt-1 transition transform ${
                          expandedId === question.id ? 'rotate-180' : ''
                        }`}
                      />
                      <div>
                        <h3 className="font-medium text-gray-900 text-left">
                          {question.normalized_question || question.original_question}
                        </h3>
                        <div className="flex items-center gap-2 mt-2 text-gray-500 text-sm flex-wrap">
                          {getQuestionCategories(question).length > 0 ? (
                            getQuestionCategories(question).map((cat) => (
                              <span key={cat} className="inline-block px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium">
                                {cat}
                              </span>
                            ))
                          ) : (
                            <span className="inline-block px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium">General</span>
                          )}
                          {adminUnlocked && question.is_canonical === false && (
                            <span className="inline-block px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-medium">
                              Hidden
                            </span>
                          )}
                          {question.status === 'pending' && (
                            <span className="inline-block px-2 py-1 bg-amber-100 text-amber-700 rounded text-xs font-medium">
                              Awaiting answer
                            </span>
                          )}
                          <div className="flex items-center gap-1">
                            <FiEye size={14} />
                            <span>{question.view_count || 0} views</span>
                          </div>
                          {question.helpful_count > 0 && (
                            <div className="flex items-center gap-1">
                              <FiThumbsUp size={14} />
                              <span>{question.helpful_count} helpful</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </button>

                {/* Answer (Expanded) */}
                {expandedId === question.id && (question.answer || question.status === 'pending' || adminUnlocked) && (
                  <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
                    {editingId === question.id ? (
                      /* --- Admin inline editor --- */
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Question (displayed)</label>
                        <textarea
                          value={editQuestion}
                          onChange={(e) => setEditQuestion(e.target.value)}
                          rows={2}
                          className="w-full border border-gray-300 rounded px-3 py-2 text-sm mb-1"
                        />
                        <button
                          onClick={() => suggestImprovement('question')}
                          disabled={suggestingField === 'question'}
                          className="text-xs text-indigo-600 hover:text-indigo-800 underline mb-3 disabled:opacity-50"
                        >
                          {suggestingField === 'question' ? 'Thinking…' : '✨ Suggest a better version'}
                        </button>
                        {questionSuggestion && (
                          <div className="mb-3 bg-indigo-50 border border-indigo-200 rounded p-3">
                            <p className="text-xs font-medium text-indigo-700 mb-1">Claude suggests:</p>
                            <p className="text-sm text-gray-800 mb-2">{questionSuggestion}</p>
                            <div className="flex gap-2">
                              <button
                                onClick={() => { setEditQuestion(questionSuggestion); setQuestionSuggestion(null); }}
                                className="text-xs bg-indigo-600 text-white px-3 py-1 rounded hover:bg-indigo-700"
                              >
                                Use this
                              </button>
                              <button
                                onClick={() => setQuestionSuggestion(null)}
                                className="text-xs text-gray-600 px-3 py-1 rounded border border-gray-300 hover:bg-gray-100"
                              >
                                Dismiss
                              </button>
                            </div>
                          </div>
                        )}
                        <label className="block text-sm font-medium text-gray-700 mb-1">Categories</label>
                        <p className="text-xs text-gray-500 mb-2">
                          Click to select one or more. A FAQ can belong to several categories.
                          <button
                            onClick={suggestCategories}
                            disabled={suggestingCats}
                            className="ml-2 text-indigo-600 hover:text-indigo-800 underline disabled:opacity-50"
                          >
                            {suggestingCats ? 'Thinking…' : '✨ Suggest categories'}
                          </button>
                        </p>
                        <div className="flex flex-wrap gap-2 mb-3">
                          {categories.map(cat => {
                            const selected = editCategories.includes(cat.name);
                            return (
                              <button
                                key={cat.id || cat.name}
                                type="button"
                                onClick={() => {
                                  setEditCategories(selected
                                    ? editCategories.filter(c => c !== cat.name)
                                    : [...editCategories, cat.name]);
                                }}
                                className={`text-xs px-3 py-1 rounded-full border transition ${
                                  selected
                                    ? 'bg-indigo-600 text-white border-indigo-600'
                                    : 'bg-white text-gray-700 border-gray-300 hover:border-indigo-400'
                                }`}
                              >
                                {selected ? '✓ ' : ''}{cat.name}
                              </button>
                            );
                          })}
                          {/* Include any current categories not in the master list as selected chips */}
                          {editCategories.filter(c => !categories.some(cat => cat.name === c)).map(c => (
                            <button
                              key={c}
                              type="button"
                              onClick={() => setEditCategories(editCategories.filter(x => x !== c))}
                              className="text-xs px-3 py-1 rounded-full border bg-indigo-600 text-white border-indigo-600"
                            >
                              ✓ {c}
                            </button>
                          ))}
                        </div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Answer</label>
                        <textarea
                          value={editAnswer}
                          onChange={(e) => setEditAnswer(e.target.value)}
                          rows={5}
                          className="w-full border border-gray-300 rounded px-3 py-2 text-sm mb-1"
                        />
                        <button
                          onClick={() => suggestImprovement('answer')}
                          disabled={suggestingField === 'answer'}
                          className="text-xs text-indigo-600 hover:text-indigo-800 underline disabled:opacity-50"
                        >
                          {suggestingField === 'answer' ? 'Thinking…' : '✨ Suggest a better version'}
                        </button>
                        {answerSuggestion && (
                          <div className="mt-2 mb-1 bg-indigo-50 border border-indigo-200 rounded p-3">
                            <p className="text-xs font-medium text-indigo-700 mb-1">Claude suggests (facts preserved):</p>
                            <p className="text-sm text-gray-800 mb-2 whitespace-pre-wrap">{answerSuggestion}</p>
                            <div className="flex gap-2">
                              <button
                                onClick={() => { setEditAnswer(answerSuggestion); setAnswerSuggestion(null); }}
                                className="text-xs bg-indigo-600 text-white px-3 py-1 rounded hover:bg-indigo-700"
                              >
                                Use this
                              </button>
                              <button
                                onClick={() => setAnswerSuggestion(null)}
                                className="text-xs text-gray-600 px-3 py-1 rounded border border-gray-300 hover:bg-gray-100"
                              >
                                Dismiss
                              </button>
                            </div>
                          </div>
                        )}
                        <p className="text-xs text-gray-500 mt-2">
                          Admin edits are saved exactly as written (no automatic rewriting).
                        </p>
                        {adminError && <p className="text-red-600 text-sm mt-2">{adminError}</p>}
                        <div className="mt-3 flex gap-2">
                          <button
                            onClick={() => saveEdit(question.id)}
                            disabled={savingEdit}
                            className="bg-indigo-600 text-white text-sm px-4 py-2 rounded hover:bg-indigo-700 disabled:opacity-50"
                          >
                            {savingEdit ? 'Saving…' : 'Save changes'}
                          </button>
                          <button
                            onClick={cancelEditing}
                            className="text-sm text-gray-600 px-4 py-2 rounded border border-gray-300 hover:bg-gray-100"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      /* --- Normal answer display --- */
                      <div>
                        {question.status === 'pending' ? (
                          adminUnlocked ? (
                            /* Admin can answer a pending question right here */
                            answeringId === question.id ? (
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Your answer</label>
                                <textarea
                                  value={pendingAnswer}
                                  onChange={(e) => setPendingAnswer(e.target.value)}
                                  rows={5}
                                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                                  placeholder="Type the answer to this question…"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                  Your answer will be cleaned up by AI (facts preserved) and the person who asked will be notified.
                                </p>
                                {adminError && <p className="text-red-600 text-sm mt-2">{adminError}</p>}
                                <div className="mt-3 flex gap-2">
                                  <button
                                    onClick={() => submitAnswer(question.id)}
                                    disabled={savingAnswer}
                                    className="bg-green-600 text-white text-sm px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50"
                                  >
                                    {savingAnswer ? 'Saving…' : 'Submit answer'}
                                  </button>
                                  <button
                                    onClick={cancelAnswering}
                                    className="text-sm text-gray-600 px-4 py-2 rounded border border-gray-300 hover:bg-gray-100"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div>
                                <p className="text-amber-700 text-sm italic mb-3">
                                  ⏳ This question hasn't been answered yet.
                                </p>
                                <button
                                  onClick={() => startAnswering(question)}
                                  className="bg-green-600 text-white text-sm px-4 py-2 rounded hover:bg-green-700"
                                >
                                  Answer this question
                                </button>
                              </div>
                            )
                          ) : (
                            <p className="text-amber-700 text-sm italic">
                              ⏳ This question hasn't been answered yet. Our team will respond soon.
                            </p>
                          )
                        ) : (
                          <>
                            {question.summary && (
                              <div className="mb-3 bg-blue-50 border-l-4 border-blue-400 px-3 py-2 rounded">
                                <p className="text-xs font-semibold text-blue-700 mb-1">Quick answer</p>
                                <p className="text-sm text-gray-800">{question.summary}</p>
                              </div>
                            )}
                            <p className="text-sm font-medium text-gray-700 mb-2">Answer:</p>
                            <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                              {question.answer || <span className="text-gray-400 italic">No answer yet</span>}
                            </p>
                          </>
                        )}
                        {relatedResources(question).length > 0 && (
                          <div className="mt-4 bg-indigo-50 border border-indigo-100 rounded-lg p-3">
                            <p className="text-xs font-semibold text-indigo-700 mb-2">📄 Related resources</p>
                            <ul className="space-y-1">
                              {relatedResources(question).map(r => (
                                <li key={r.id}>
                                  <a href={r.url} target="_blank" rel="noopener noreferrer"
                                    className="text-sm text-indigo-700 hover:text-indigo-900 underline inline-flex items-center gap-1">
                                    {r.title}
                                    {r.type && <span className="text-xs text-indigo-500">({r.type})</span>}
                                  </a>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        <div className="mt-4 pt-4 border-t border-gray-200 flex gap-3 items-center">
                          {question.status !== 'pending' && (
                            <button className="text-sm text-gray-600 hover:text-green-600 transition flex items-center gap-1">
                              <FiThumbsUp size={16} />
                              Helpful
                            </button>
                          )}
                          {adminUnlocked && (
                            <>
                              {question.status !== 'pending' && (
                                <button
                                  onClick={() => startEditing(question)}
                                  className="text-sm text-indigo-600 hover:text-indigo-800 underline"
                                >
                                  Edit
                                </button>
                              )}
                              <button
                                onClick={() => toggleVisibility(question.id, question.is_canonical)}
                                className="text-sm text-gray-600 hover:text-gray-900 underline"
                              >
                                {question.is_canonical ? 'Hide from Browse' : 'Show in Browse'}
                              </button>
                              {question.is_canonical === false && (
                                <button
                                  onClick={() => runUnmerge(question.id)}
                                  className="text-sm text-amber-700 hover:text-amber-900 underline"
                                  title="If this was wrongly merged into another question, restore it as its own separate entry."
                                >
                                  Restore as separate question
                                </button>
                              )}
                              <button
                                onClick={() => deleteQuestion(question.id)}
                                className="text-sm text-red-600 hover:text-red-800 underline"
                              >
                                Delete
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
        <p className="text-blue-900 font-medium mb-2">Didn't find what you're looking for?</p>
        <p className="text-blue-800 text-sm mb-4">Submit your question and our team will provide an answer.</p>
        <a
          href="/submit"
          className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition"
        >
          Submit a Question
        </a>
      </div>
    </div>
  );
}

export default Browse;
